// src/lib/presets.ts
/**
 * DNL Context Preset Definitions
 * Phase 6: Context Presets
 * 
 * Pre-configured settings for common ratio scenarios
 * Based on DNL Enhancement Specification v1.0, Section 3.8
 */

import type { PresetConfiguration, DNLContextPreset } from '../types/dnl';
import type { NumberFormatConfig } from '../types/redux';

// =============================================================================
// PRESET CONFIGURATIONS
// =============================================================================

/**
 * All available context presets
 * Maps preset IDs to their configuration
 */
export const CONTEXT_PRESETS: Record<DNLContextPreset, PresetConfiguration> = {
  recipe: {
    name: 'Recipe Scaling',
    description: 'Scale ingredients based on number of people',
    primaryLabel: 'People',
    primarySubtext: undefined,
    secondaryLabel: 'Ingredient',
    secondarySubtext: '(amount)',
    primaryFormat: {
      type: 'integer',
    },
    secondaryFormat: {
      type: 'decimal',
      decimalPlaces: 1,
    },
    suggestedScaleFactor: undefined, // User-defined based on recipe
    icon: 'ChefHat',
  },
  
  'metric-length': {
    name: 'Metric Length Conversion',
    description: 'Convert between centimeters and millimeters',
    primaryLabel: 'Centimeters',
    primarySubtext: '(cm)',
    secondaryLabel: 'Millimeters',
    secondarySubtext: '(mm)',
    primaryFormat: {
      type: 'integer',
    },
    secondaryFormat: {
      type: 'integer',
    },
    suggestedScaleFactor: { numerator: 10, denominator: 1 }, // 1 cm = 10 mm
    icon: 'Ruler',
  },
  
  percentage: {
    name: 'Percentage & Amount',
    description: 'Relate percentages to actual amounts (e.g., discounts, tips)',
    primaryLabel: 'Percentage',
    primarySubtext: '(%)',
    secondaryLabel: 'Amount',
    secondarySubtext: undefined,
    primaryFormat: {
      type: 'percentage',
      decimalPlaces: 0,
    },
    secondaryFormat: {
      type: 'currency',
      currencySymbol: '£',
      currencyPosition: 'before',
    },
    suggestedScaleFactor: undefined, // Depends on total amount
    icon: 'Percent',
  },
  
  'fraction-decimal': {
    name: 'Fraction to Decimal',
    description: 'Show equivalent fractions and decimals',
    primaryLabel: 'Fraction',
    primarySubtext: undefined,
    secondaryLabel: 'Decimal',
    secondarySubtext: undefined,
    primaryFormat: {
      type: 'fraction',
      fractionFormat: 'mixed',
    },
    secondaryFormat: {
      type: 'decimal',
      decimalPlaces: 2,
    },
    suggestedScaleFactor: { numerator: 1, denominator: 1 }, // 1:1 mapping
    icon: 'Divide',
  },
  
  enlargement: {
    name: 'Scale Factor / Enlargement',
    description: 'Geometric enlargement or reduction by a scale factor',
    primaryLabel: 'Original',
    primarySubtext: '(cm)',
    secondaryLabel: 'Enlarged',
    secondarySubtext: '(cm)',
    primaryFormat: {
      type: 'decimal',
      decimalPlaces: 1,
    },
    secondaryFormat: {
      type: 'decimal',
      decimalPlaces: 1,
    },
    suggestedScaleFactor: undefined, // User-defined scale factor
    icon: 'Maximize2',
  },
  
  currency: {
    name: 'Currency Exchange',
    description: 'Convert between two currencies',
    primaryLabel: 'Pounds (GBP)',
    primarySubtext: '(£)',
    secondaryLabel: 'Euros (EUR)',
    secondarySubtext: '(€)',
    primaryFormat: {
      type: 'currency',
      currencySymbol: '£',
      currencyPosition: 'before',
      decimalPlaces: 2,
    },
    secondaryFormat: {
      type: 'currency',
      currencySymbol: '€',
      currencyPosition: 'before',
      decimalPlaces: 2,
    },
    suggestedScaleFactor: undefined, // Based on current exchange rate
    icon: 'Coins',
  },
  
  custom: {
    name: 'Custom Configuration',
    description: 'Manually configure all settings',
    primaryLabel: '',
    primarySubtext: undefined,
    secondaryLabel: '',
    secondarySubtext: undefined,
    primaryFormat: {
      type: 'integer',
    },
    secondaryFormat: {
      type: 'integer',
    },
    suggestedScaleFactor: undefined,
    icon: 'Settings',
  },
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Get preset configuration by ID
 */
export function getPresetConfig(presetId: DNLContextPreset): PresetConfiguration {
  return CONTEXT_PRESETS[presetId];
}

/**
 * Get all available presets except 'custom'
 */
export function getAvailablePresets(): Array<{ id: DNLContextPreset; config: PresetConfiguration }> {
  return Object.entries(CONTEXT_PRESETS)
    .filter(([id]) => id !== 'custom')
    .map(([id, config]) => ({ id: id as DNLContextPreset, config }));
}

/**
 * Check if a preset has a suggested scale factor
 */
export function hasScaleFactor(presetId: DNLContextPreset): boolean {
  return CONTEXT_PRESETS[presetId].suggestedScaleFactor !== undefined;
}

/**
 * Format preset name for display
 */
export function formatPresetName(presetId: DNLContextPreset): string {
  return CONTEXT_PRESETS[presetId].name;
}






